import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MainLayout } from "./layouts/MainLayout";
import { LoginPage } from "./pages/LoginPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ParametresPage } from "./pages/ParametresPage";
import { ForecastCAPage } from "./pages/ForecastCAPage";
import { CAReelPage } from "./pages/CAReelPage";
import { PlateformesPage } from "./pages/PlateformesPage";
import { LinxoImportPage } from "./pages/LinxoImportPage";
import { DepensesPage } from "./pages/DepensesPage";
import { RecapDepensesPage } from "./pages/RecapDepensesPage";
import { TVAPage } from "./pages/TVAPage";
import { PlanningPage } from "./pages/PlanningPage";
import { SynthesePlanningPage } from "./pages/SynthesePlanningPage";
import { VerificationEspecesPage } from "./pages/VerificationEspecesPage";
import { VerifEnveloppePage } from "./pages/VerifEnveloppePage";
import { DashboardKPIPage } from "./pages/DashboardKPIPage";
import { DashboardDepensesPage } from "./pages/DashboardDepensesPage";
import { InventairePage } from "./pages/InventairePage";
import { CommandesFournisseursPage } from "./pages/CommandesFournisseursPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { TraiteurPage } from "./pages/TraiteurPage";
import { MeteoPage } from "./pages/MeteoPage";
import { SocialCalendarPage } from "./pages/SocialCalendarPage";
import { AnalyseVentesPage } from "./pages/AnalyseVentesPage";
import { RHPage } from "./pages/RHPage";
import { TransmissionPage } from "./pages/TransmissionPage";
import { RecettesSecretesPage } from "./pages/RecettesSecretesPage";
import { ProceduresPage } from "./pages/ProceduresPage";
import PilotageFinancierPage from "./pages/PilotageFinancierPage";
import { UberVerificationPage } from "./pages/UberVerificationPage";
import { QCMPage } from "./pages/QCMPage";
import FormationPage from "./pages/FormationPage";
import { BoosterMarketingPage } from "./pages/BoosterMarketingPage";
import { FidelitePage } from "./pages/FidelitePage";
import MaFidelitePage from "./pages/MaFidelitePage";
import { RentabilitePage } from "./pages/RentabilitePage";
import { MercurialePage } from "./pages/MercurialePage";
import { DecongelationPage } from "./pages/DecongelationPage";
import { HygienePage } from "./pages/HygienePage";
import { RealtimeDashboardPage } from "./pages/RealtimeDashboardPage";
import { BusinessInsightsPage } from "./pages/BusinessInsightsPage";
import { PertesPage } from "./pages/PertesPage";
import { OuverturePage, FinMidiPage, FermetureSoirPage } from "./pages/OuverturePage";
import { CalculateurPrepPage } from "./pages/CalculateurPrepPage";
import NotificationsPage from "./pages/NotificationsPage";
import { LivraisonsLaboPage } from "./pages/LivraisonsLaboPage";

function PrivateRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  // Check module permission
  const moduleId = getModuleId(window.location.pathname);
  if (moduleId && user?.role !== 'admin' && user?.permissions && !user.permissions.includes(moduleId)) {
    return <MainLayout><div className="flex items-center justify-center h-96"><p className="text-muted-foreground text-lg">Acces non autorise a ce module</p></div></MainLayout>;
  }

  return <MainLayout>{children}</MainLayout>;
}

function getModuleId(path) {
  const map = {
    '/': 'dashboard', '/parametres': 'parametres', '/forecast-ca': 'forecast-ca',
    '/ca-reel': 'ca-reel', '/plateformes': 'plateformes', '/linxo': 'linxo',
    '/depenses': 'depenses', '/recap-depenses': 'recap-depenses', '/tva': 'tva',
    '/planning': 'planning', '/synthese-planning': 'synthese-planning',
    '/verification-especes': 'verification-especes', '/dashboard-kpi': 'dashboard-kpi',
    '/dashboard-depenses': 'dashboard-depenses', '/inventaire': 'inventaire',
    '/commandes': 'commandes',
    '/livraisons-labo': 'livraisons-labo',
    '/notifications': 'notifications',
    '/traiteur': 'traiteur',
    '/meteo': 'meteo',
    '/social-calendar': 'social',
    '/analyse-ventes': 'analyse-ventes',
    '/rh': 'rh',
    '/transmission': 'transmission',
    '/recettes-secretes': 'recettes-secretes',
    '/procedures': 'procedures',
    '/uber-verification': 'plateformes',
    '/qcm': 'qcm',
    '/formation': 'formation',
    '/marketing': 'marketing',
    '/fidelite': 'fidelite',
    '/rentabilite': 'rentabilite',
    '/mercuriale': 'mercuriale',
    '/decongelation': 'decongelation',
    '/hygiene': 'hygiene',
    '/temps-reel': 'realtime',
    '/insights': 'insights',
    '/pertes': 'pertes',
    '/ouverture': 'ouverture',
    '/fin-midi': 'fin-midi',
    '/fermeture-soir': 'fermeture-soir',
    '/calculateur-prep': 'calculateur-prep',
  };
  return map[path] || null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/parametres" element={<PrivateRoute><ParametresPage /></PrivateRoute>} />
      <Route path="/forecast-ca" element={<PrivateRoute><ForecastCAPage /></PrivateRoute>} />
      <Route path="/ca-reel" element={<PrivateRoute><CAReelPage /></PrivateRoute>} />
      <Route path="/plateformes" element={<PrivateRoute><PlateformesPage /></PrivateRoute>} />
      <Route path="/linxo" element={<PrivateRoute><LinxoImportPage /></PrivateRoute>} />
      <Route path="/depenses" element={<PrivateRoute><DepensesPage /></PrivateRoute>} />
      <Route path="/recap-depenses" element={<PrivateRoute><RecapDepensesPage /></PrivateRoute>} />
      <Route path="/tva" element={<PrivateRoute><TVAPage /></PrivateRoute>} />
      <Route path="/planning" element={<PrivateRoute><PlanningPage /></PrivateRoute>} />
      <Route path="/synthese-planning" element={<PrivateRoute><SynthesePlanningPage /></PrivateRoute>} />
      <Route path="/verification-especes" element={<PrivateRoute><VerificationEspecesPage /></PrivateRoute>} />
      <Route path="/verification-enveloppe" element={<PrivateRoute><VerifEnveloppePage /></PrivateRoute>} />
      <Route path="/dashboard-kpi" element={<PrivateRoute><DashboardKPIPage /></PrivateRoute>} />
      <Route path="/dashboard-depenses" element={<PrivateRoute><DashboardDepensesPage /></PrivateRoute>} />
      <Route path="/inventaire" element={<PrivateRoute><InventairePage /></PrivateRoute>} />
      <Route path="/commandes" element={<PrivateRoute><CommandesFournisseursPage /></PrivateRoute>} />
      <Route path="/livraisons-labo" element={<PrivateRoute><LivraisonsLaboPage /></PrivateRoute>} />
      <Route path="/traiteur" element={<PrivateRoute><TraiteurPage /></PrivateRoute>} />
      <Route path="/meteo" element={<PrivateRoute><MeteoPage /></PrivateRoute>} />
      <Route path="/social-calendar" element={<PrivateRoute><SocialCalendarPage /></PrivateRoute>} />
      <Route path="/analyse-ventes" element={<PrivateRoute><AnalyseVentesPage /></PrivateRoute>} />
      <Route path="/rh" element={<PrivateRoute><RHPage /></PrivateRoute>} />
      <Route path="/transmission" element={<PrivateRoute><TransmissionPage /></PrivateRoute>} />
      <Route path="/recettes-secretes" element={<PrivateRoute><RecettesSecretesPage /></PrivateRoute>} />
      <Route path="/procedures" element={<PrivateRoute><ProceduresPage /></PrivateRoute>} />
      <Route path="/admin/users" element={<PrivateRoute><UserManagementPage /></PrivateRoute>} />
      <Route path="/pilotage" element={<PrivateRoute><PilotageFinancierPage /></PrivateRoute>} />
      <Route path="/uber-verification" element={<PrivateRoute><UberVerificationPage /></PrivateRoute>} />
      <Route path="/qcm" element={<PrivateRoute><QCMPage /></PrivateRoute>} />
      <Route path="/formation" element={<PrivateRoute><FormationPage /></PrivateRoute>} />
      <Route path="/marketing" element={<PrivateRoute><BoosterMarketingPage /></PrivateRoute>} />
      <Route path="/fidelite" element={<PrivateRoute><FidelitePage /></PrivateRoute>} />
      <Route path="/rentabilite" element={<PrivateRoute><RentabilitePage /></PrivateRoute>} />
      <Route path="/mercuriale" element={<PrivateRoute><MercurialePage /></PrivateRoute>} />
      <Route path="/decongelation" element={<PrivateRoute><DecongelationPage /></PrivateRoute>} />
      <Route path="/hygiene" element={<PrivateRoute><HygienePage /></PrivateRoute>} />
      <Route path="/temps-reel" element={<PrivateRoute><RealtimeDashboardPage /></PrivateRoute>} />
      <Route path="/insights" element={<PrivateRoute><BusinessInsightsPage /></PrivateRoute>} />
      <Route path="/pertes" element={<PrivateRoute><PertesPage /></PrivateRoute>} />
      <Route path="/ouverture" element={<PrivateRoute><OuverturePage /></PrivateRoute>} />
      <Route path="/fin-midi" element={<PrivateRoute><FinMidiPage /></PrivateRoute>} />
      <Route path="/fermeture-soir" element={<PrivateRoute><FermetureSoirPage /></PrivateRoute>} />
      <Route path="/calculateur-prep" element={<PrivateRoute><CalculateurPrepPage /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
      <Route path="/ma-fidelite/:clientId" element={<MaFidelitePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
